import { Autocomplete, FormControlLabel, Radio, RadioGroup, TextField } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { NewLayout } from '../components/newLayout/NewLayout'
import CardWrapper from '../components/common/CardWrapper'
import { LoadingIndicator } from '../components/common/LoadingIndicator'
import { ActionAlert } from '../components/dialogs/ActionAlert'
import { ActionFailedAlert } from '../components/dialogs/ActionFailedAlert'
import { ApexDocumentForm } from '../components/documents/ApexDocumentForm'
import { ImperiumDocumentForm } from '../components/documents/ImperiumDocumentForm'
import { NewClientForm, NewClientOptions } from '../components/documents/NewClientForm'
import { ApexTemplate, ImperiumTemplate } from '../api/documentApi'
import { downloadBase64File, useDocumentApi } from '../hooks/useDocumentApi'
import { useClientApi } from '../hooks/useClientApi'
import { useSelector } from '../redux/hooks/useSelector'
import { Client } from '../redux/reducers/clients'
import { APEX, IMPERIUM_FINANCE } from '../constants/businessTerritory'
import { ACTION_SUCESS_ALERT_ID } from '../constants/dialogConstants'
import { openDialogMessage } from '../redux/actions/dialogActions'
import { tokens } from '../styles/tokens'

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  clientRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  autocomplete: {
    minWidth: '320px',
    flexGrow: 1,
    maxWidth: '480px',
  },
  error: {
    margin: 0,
    fontSize: '0.8rem',
    color: tokens.color.error[500],
  },
}))

export const Documents = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()

  const user = useSelector(state => state.auth.user)
  const clients = useSelector(state => state.clients.clients)
  const loaders = useSelector(state => state.loading.isLoading)
  const isGenerating = !!loaders.find(loader => loader === 'generateDocuments')

  const { getClients } = useClientApi()
  const { getDocumentTemplates, generateDocuments } = useDocumentApi()

  const [clientMode, setClientMode] = useState<'existing' | 'new'>('existing')
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [newClient, setNewClient] = useState<Partial<Client>>({ company: false })
  const [newClientOptions, setNewClientOptions] = useState<NewClientOptions>({
    sendCredentials: false,
    sendWelcomeEmail: false,
  })
  const [apexTemplates, setApexTemplates] = useState<ApexTemplate[]>([])
  const [imperiumTemplates, setImperiumTemplates] = useState<ImperiumTemplate[]>([])
  const [templatesLoaded, setTemplatesLoaded] = useState(false)
  const [validationError, setValidationError] = useState<string>('')

  const isApexTerritory = user && user.businessTerritoryId === APEX
  const isImperiumTerritory = user && user.businessTerritoryId === IMPERIUM_FINANCE

  useEffect(() => {
    if (!clients || clients.length === 0) {
      getClients()
    }
  }, [])

  useEffect(() => {
    if (!user || templatesLoaded || (!isApexTerritory && !isImperiumTerritory)) return
    setTemplatesLoaded(true)
    if (isApexTerritory) {
      getDocumentTemplates<ApexTemplate[]>().then(templates => setApexTemplates(templates || []))
    } else {
      getDocumentTemplates<ImperiumTemplate[]>().then(templates => setImperiumTemplates(templates || []))
    }
  }, [user])

  // Predvyber klienta z query parametru (?clientId=...) — proklik z detailu klienta
  useEffect(() => {
    const clientIdParam = new URLSearchParams(location.search).get('clientId')
    if (!clientIdParam || !clients || clients.length === 0) return
    const found = clients.find(item => item.clientId === Number(clientIdParam))
    if (found) {
      setSelectedClient(found)
      setClientMode('existing')
    }
  }, [clients, location.search])

  const clientLabel = (client: Client) => {
    const name = client.company
      ? `${client.companyName || ''} (${client.companyId || ''})`
      : `${client.firstName || ''} ${client.surname || ''} (${client.personalNumber || ''})`
    return name
  }

  const handleUseExisting = (client: Client) => {
    setSelectedClient(client)
    setClientMode('existing')
  }

  const handleGenerate = async (
    payload: { sablona?: string; dokumenty?: string[]; data: Record<string, any> },
    format: 'pdf' | 'docx'
  ) => {
    setValidationError('')
    if (clientMode === 'existing' && !selectedClient) {
      setValidationError(t('SELECT_CLIENT_FIRST'))
      return
    }
    if (clientMode === 'new') {
      const hasIdentifier = newClient.company ? !!newClient.companyId : !!newClient.personalNumber
      if (!newClient.surname || !hasIdentifier) {
        setValidationError(t('NEW_CLIENT_MANDATORY_FIELDS'))
        return
      }
    }

    const hasEmail = !!(newClient.email || '').trim()
    const response = await generateDocuments({
      clientId: clientMode === 'existing' ? selectedClient?.clientId : undefined,
      client: clientMode === 'new' ? newClient : undefined,
      sablona: payload.sablona,
      dokumenty: payload.dokumenty,
      format,
      data: payload.data,
      sendCredentials: clientMode === 'new' && hasEmail && newClientOptions.sendCredentials,
      sendWelcomeEmail: clientMode === 'new' && hasEmail && newClientOptions.sendWelcomeEmail,
    })
    if (!response) return

    downloadBase64File(response.file)
    let message = t('DOCUMENT_SAVED_TO_ARCHIVE')
    if (clientMode === 'new') {
      message += ' ' + (response.clientIncomplete ? t('CLIENT_CREATED_INCOMPLETE') : t('CLIENT_CREATED'))
      if (response.credentialsSent) message += ' ' + t('CREDENTIALS_SENT')
      if (response.welcomeEmailSent) message += ' ' + t('WELCOME_EMAIL_SENT')
      if (response.credentialsSent === false || response.welcomeEmailSent === false)
        message += ' ' + t('CLIENT_EMAILS_FAILED') + (response.clientEmailError ? ` (${response.clientEmailError})` : '')
      // Nove zalozeny klient -> prepnout na "existujici", at dalsi generovani nezaklada duplikat
      await getClients()
      setClientMode('existing')
      setNewClient({ company: false })
      setNewClientOptions({ sendCredentials: false, sendWelcomeEmail: false })
    }
    dispatch(openDialogMessage(ACTION_SUCESS_ALERT_ID, message))
  }

  return (
    <NewLayout pageName="DOCUMENTS">
      <CardWrapper className={classes.card}>
        <RadioGroup
          row
          value={clientMode}
          onChange={event => setClientMode(event.target.value as 'existing' | 'new')}
        >
          <FormControlLabel value="existing" control={<Radio />} label={t('EXISTING_CLIENT')} />
          <FormControlLabel value="new" control={<Radio />} label={t('NEW_CLIENT')} />
        </RadioGroup>

        {clientMode === 'existing' && (
          <div className={classes.clientRow}>
            <Autocomplete
              className={classes.autocomplete}
              options={clients || []}
              value={selectedClient}
              getOptionLabel={clientLabel}
              isOptionEqualToValue={(option, item) => option.clientId === item.clientId}
              onChange={(event, item: Client | null) => setSelectedClient(item)}
              renderInput={params => <TextField {...params} variant="standard" label={t('CLIENT')} />}
            />
          </div>
        )}

        {clientMode === 'new' && (
          <NewClientForm
            value={newClient}
            onChange={setNewClient}
            onUseExisting={handleUseExisting}
            options={newClientOptions}
            onOptionsChange={setNewClientOptions}
          />
        )}

        {validationError && <p className={classes.error}>{validationError}</p>}
      </CardWrapper>

      <CardWrapper className={classes.card}>
        {!templatesLoaded && <LoadingIndicator />}
        {isApexTerritory && (
          <ApexDocumentForm
            templates={apexTemplates}
            client={clientMode === 'existing' ? selectedClient : newClient}
            loading={isGenerating}
            onGenerate={handleGenerate}
          />
        )}
        {isImperiumTerritory && (
          <ImperiumDocumentForm
            templates={imperiumTemplates}
            client={clientMode === 'existing' ? selectedClient : newClient}
            loading={isGenerating}
            onGenerate={handleGenerate}
          />
        )}
      </CardWrapper>

      <ActionAlert />
      <ActionFailedAlert handleClose={() => {}} />
    </NewLayout>
  )
}
